import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Navbar from "./components/Navbar";
import SearchComponent from "./components/SearchComponent";
import Sidebar from "./components/Sidebar";
import PageContent from "./components/PageContent";
import {
  getAllHelpPageData,
  getSearchedResult,
} from "../../Redux/actions/HelpingPageAction";
import { postRequest } from "../../services/axios";
import { toast } from "react-toastify";
import "./Styles/style.css";

const HelpingPageLayout = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();
  const { allHelpPageContent, searchedResult } = useSelector(
    (state) => state.HelpingPageReducer
  );
  const [loading, setLoading] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [selectedHeading, setSelectedHeading] = useState(null);
  const [openSubheadingId, setOpenSubheadingId] = useState(null);
  const [tertiaryId, setTertiaryId] = useState("");
  const [currPageContent, setCurrPageContent] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState(searchQuery);
  const [searchLoading, setSearchLoading] = useState(false);
  const [loadPageContent, setLoadPageContent] = useState(false);
  const [mainHeading, setMainHeading] = useState("Getting Started");
  const [secondaryHeading, setSecondaryHeading] = useState("");
  const [tertiarySubHeading, setTertiarySubHeading] = useState("");
  const [headingClicked, setHeadingClicked] = useState(true);

  // Fetch all help page content
  const fetchContent = async () => {
    try {
      setLoading(true);
      dispatch(getAllHelpPageData()).then(() => {
        setLoading(false);
      });
    } catch (error) {
      setLoading(false);
    }
  };

  const toggleSubheading = (parentHeadingId, subHeadingId) => {
    const newOpenId = `${parentHeadingId}-${subHeadingId}`;
    setOpenSubheadingId((prevId) => (prevId === newOpenId ? null : newOpenId));

    const newSelectedHeading = allHelpPageContent.find(
      (data) => data._id === parentHeadingId
    );
    setSelectedHeading(newSelectedHeading);
  };

  // Fetch content by ID (used when selecting search result or heading)
  const fetchContentById = async (id) => {
    try {
      setLoadPageContent(true);
      const response = await postRequest(`/help/getContentById/${id}`);
      if (response.data.success) {
        setLoadPageContent(false);
        navigate(`/help/${id}`);
        setCurrPageContent(response.data.tertiarySubHeading.content);
        setTertiarySubHeading(
          response.data.tertiarySubHeading.tertiarySubHeading
        );
        setSecondaryHeading(response.data.secondaryHeading);
        setMainHeading(response.data.mainHeading);
        const newOpenId = `${response.data.mainId}-${response.data.subHeadingId}`;
        setOpenSubheadingId(newOpenId); // Open the dropdown
        setTertiaryId(id);
        setHeadingClicked(false);
      }
      toast.success("Content fetched");
    } catch (err) {
      toast.error("Error in fetching content");
    }
  };

  // When the page loads, fetch content by ID from URL params
  useEffect(() => {
    if (id) fetchContentById(id);
    else if(tertiaryId){
      fetchContentById(tertiaryId);
    }
  }, [id, tertiaryId]);

  // Fetch all help page data when the component mounts
  useEffect(() => {
    fetchContent();
  }, []);

  // Set the selected heading when all help page content is loaded
  useEffect(() => {
    if (allHelpPageContent && allHelpPageContent.length > 0) {
      setSelectedHeading(allHelpPageContent[0]);
    }
  }, [allHelpPageContent]);

  // Debounce search query to avoid triggering too many searches
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  // Handle search input
  const handleSearch = () => {
    setSearchLoading(true);
    if (debouncedQuery !== "") {
      dispatch(getSearchedResult(searchQuery)).then(() => {
        setSearchLoading(false);
      });
    }
  };

  // Trigger search whenever the debounced search query changes
  useEffect(() => {
    handleSearch();
  }, [debouncedQuery, dispatch]);

  // Handle search result click
  const handleSearchResultClick = (resultId) => {
    fetchContentById(resultId); // Fetch content based on the result clicked
  };

  return (
    <div className="main-help-container">
      <section className="w-full px-4 HelperNavBar" style={{ height: "var(--app-navbar-height, 58px)", minHeight: "var(--app-navbar-height, 58px)", flexShrink: 0 }}>
        <Navbar isSearchOpen={isSearchOpen} setIsSearchOpen={setIsSearchOpen} />
      </section>

      <div className="flex w-full Hepler-mainDivContainer" style={{ height: "calc(100% - var(--app-navbar-height, 58px))" }}>
        <section className="w-[18%] pl-5">
          <Sidebar
            helpPageData={allHelpPageContent}
            setSelectedHeading={setSelectedHeading}
            toggleSubheading={toggleSubheading}
            openSubheadingId={openSubheadingId}
            setOpenSubheadingId={setOpenSubheadingId}
            tertiaryId={tertiaryId}
            setTertiaryId={setTertiaryId}
            setHeadingClicked={setHeadingClicked}
            loadPageContent={loadPageContent}
            setMainHeading={setMainHeading}
          />
        </section>

        <section className="h-full w-[82%] text-[#707070]">
          <PageContent
            currPageContent={currPageContent}
            selectedHeading={selectedHeading}
            toggleSubheading={toggleSubheading}
            openSubheadingId={openSubheadingId}
            tertiaryId={tertiaryId}
            setTertiaryId={setTertiaryId}
            loadPageContent={loadPageContent}
            secondaryHeading={secondaryHeading}
            tertiarySubHeading={tertiarySubHeading}
            mainHeading={mainHeading}
            headingClicked={headingClicked}
            setCurrPageContent={setCurrPageContent}
          />
        </section>
      </div>

      {isSearchOpen && (
        <div>
          <div
            className="seach-HelperLay"
            onClick={() => setIsSearchOpen(!isSearchOpen)}
          ></div>
          <div className="search-overlay">
            <SearchComponent
              searchedResult={searchedResult}
              setSearchQuery={setSearchQuery}
              searchQuery={searchQuery}
              searchLoading={searchLoading}
              onResultClick={handleSearchResultClick}
              setIsSearchOpen={setIsSearchOpen}
              setHeadingClicked={setHeadingClicked}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpingPageLayout;
